import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { PMPagination } from "../../../utils/PMPagination";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { TWclassNames } from "../../../utils/Div";
import { CustomerTitle, LoadingRows, ProjectTitle } from "../../../Utils";
import { ChecklistStatus, checklistStatusOptions } from "./utils";
import { ResourceEmpty } from "../../../utils/ResourceEmpty";
import { PMDataTable } from "../../../utils/PMTableView/PMDataTable";
import { CellLink } from "../../../utils/PMTableView/CellLink";

export const ChecklistsTable = (props) => {
  const { itemState, onPageChange, itemsCount, currentCount, page, perPage } =
    props;
  const { data, isLoading, errors } = itemState;
  const checklists = data;
  const checkbox = useRef();
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedReports, setSelectedReports] = useState([]);

  useLayoutEffect(() => {
    const isIndeterminate =
      selectedReports.length > 0 && selectedReports.length < checklists.length;
    setChecked(selectedReports.length === checklists.length);
    setIndeterminate(isIndeterminate);
    checkbox.current.indeterminate = isIndeterminate;
  }, [selectedReports]);

  function toggleAll() {
    setSelectedReports(checked || indeterminate ? [] : checklists);
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const navigate = useNavigate();

  const renderRows = () => {
    return checklists?.map((checklist) => (
      <tr
        key={checklist?.id}
        className={TWclassNames(
          selectedReports.includes(checklist) ? "bg-gray-50" : null,
          "hover:bg-gray-50"
        )}
      >
        <td className="relative w-12 px-6 sm:w-16 sm:px-8 hidden md:table-cell">
          {selectedReports.includes(checklist) && (
            <div className="absolute inset-y-0 left-0 w-0.5 bg-indigo-600" />
          )}
          <input
            type="checkbox"
            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
            value={data.id}
            checked={selectedReports.includes(checklist)}
            onChange={(e) =>
              setSelectedReports(
                e.target.checked
                  ? [...selectedReports, checklist]
                  : selectedReports.filter((p) => p !== checklist)
              )
            }
          />
        </td>
        <td
          onClick={() =>
            navigate("/dashboard/hse-ks/checklists/" + checklist.id)
          }
          className={TWclassNames(
            "whitespace-nowrap py-4 pr-3 px-10 sm:px-0 text-sm font-medium",
            selectedReports.includes(checklist)
              ? "text-indigo-600"
              : "text-gray-900"
          )}
        >
          <div className="flex items-center">
            <div className="font-medium text-gray-900">
              <div className="flex flex-col">
                <div>{checklist?.number}</div>
                <div>{checklist?.checklist_type?.displayTitle}</div>
                <div className="text-gray-500">{checklist?.title}</div>
                <div className="visible md:invisible">
                  <ProjectTitle
                    project={checklist?.project}
                    folder={checklist?.folder}
                  />
                </div>
              </div>
            </div>
          </div>
        </td>
        <td className="hidden md:table-cell whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <div className="flex flex-col">
            <div>
              <ProjectTitle
                project={checklist?.project}
                folder={checklist?.folder}
              />
            </div>
            <div>
              <CustomerTitle customer={checklist?.customer} />
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
          <ChecklistStatus checklist={checklist} />
        </td>
      </tr>
    ));
  };

  return (
    <div>
      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount}
        showCount={false}
        showPages={false}
      />
      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="relative overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full table-fixed divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="hidden md:table-cell">
                      <input
                        type="checkbox"
                        className="left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 sm:left-6"
                        ref={checkbox}
                        checked={checked}
                        onChange={toggleAll}
                      />
                    </th>
                    <th
                      scope="col"
                      className=" px-10 sm:px-0 py-3.5 pr-3 text-left text-sm font-semibold text-gray-900"
                    >
                      <div className="flex flex-col">
                        <div>Number</div>
                        <div>Type</div>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="hidden md:table-cell px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      <div className="flex flex-col">
                        <div>Project</div>
                        <div>Customer</div>
                      </div>
                    </th>

                    <th
                      scope="col"
                      className="px-3 py-3.5 text-right text-sm font-semibold text-gray-900"
                    >
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {isLoading ? (
                    <>
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                      <LoadingRows cols={3} checkboxCell={true} />
                    </>
                  ) : data.length < 1 ? (
                    <tr>
                      <td colSpan={4} className="py-2 px-2">
                        <ResourceEmpty />
                      </td>
                    </tr>
                  ) : (
                    renderRows()
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <PMPagination
        disabled={isLoading}
        onPageChange={onPageChange}
        page={page}
        perPage={perPage}
        rowsCount={itemsCount}
        totalCount={itemsCount}
        currentCount={currentCount}
        showCount={true}
      />
    </div>
  );
};

ChecklistsTable.defaultProps = {
  page: 1,
  itemState: [],
  itemsCount: 0,
  selectedItems: [],
  sort: "desc",
  sortBy: "createdAt",
};

// ─────────────────────────────────────────────────────────────────────────────
// New Promang Table View (schema-driven) implementation for the checklist list.
// ─────────────────────────────────────────────────────────────────────────────

const checklistStatusLabel = (value) => {
  const match = checklistStatusOptions.find(
    (o) => String(o.value) === String(value)
  );
  return match ? match.label : value;
};

// Full column definitions (with cell renderers + grouping). Exported so the
// list page can derive column metadata for the toolbar / useTableView hook.
export const getChecklistColumns = (t) => [
  {
    key: "number",
    label: t("Number"),
    sortKey: "number",
    width: 220,
    always: true,
    cardRole: "title",
    cell: (item) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <CellLink
          to={item?.id ? `/dashboard/hse-ks/checklists/${item.id}` : null}
          className="text-[13.5px] font-semibold tabular-nums text-blue-600"
        >
          {item?.number || "—"}
        </CellLink>
        {item?.title && (
          <span className="truncate text-[12px] text-slate-500">
            {item.title}
          </span>
        )}
      </div>
    ),
  },
  {
    key: "status",
    label: t("Status"),
    sortKey: "status",
    width: 140,
    stopRowClick: true,
    groupable: true,
    cardRole: "status",
    groupValue: (item) => item?.status || "",
    groupLabel: (key) => checklistStatusLabel(key) || t("table_view.no_value"),
    cell: (item) => <ChecklistStatus checklist={item} />,
  },
  {
    key: "checklist_type",
    label: t("Type"),
    width: 160,
    cardRole: "meta",
    groupable: true,
    groupValue: (item) => item?.checklist_type?.displayTitle || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => (
      <span className="block max-w-[160px] truncate text-[13px] text-slate-600">
        {item?.checklist_type?.displayTitle || "—"}
      </span>
    ),
  },
  {
    key: "created_at",
    label: t("Created"),
    sortKey: "created_at",
    width: 140,
    cardRole: "meta",
    cell: (item) => (
      <span className="text-[13px] tabular-nums text-slate-500">
        {item?.created_at
          ? new Date(item.created_at).toLocaleDateString("nb-NO", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "—"}
      </span>
    ),
  },
  {
    key: "project",
    label: t("Customer / Project"),
    width: 240,
    groupable: true,
    cardRole: "detail",
    groupValue: (item) => item?.customer?.displayTitle || "",
    groupLabel: (key) => key || t("table_view.no_value"),
    cell: (item) => (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-[13px] text-slate-700">
          <ProjectTitle project={item?.project} folder={item?.folder} />
        </span>
        <CellLink
          to={item?.customer?.id ? `/dashboard/customers/${item.customer.id}` : null}
          className="truncate text-[12px] text-slate-400"
        >
          <CustomerTitle customer={item?.customer} />
        </CellLink>
      </div>
    ),
  },
];

export const ChecklistsDataTable = ({
  itemState = { data: [], isLoading: false },
  columns,
  visibleKeys,
  widths,
  onResize,
  groupBy = "none",
  sort = null,
  onSort,
  density = "comfortable",
  hasMore = false,
  onLoadMore,
  onItemsSelect = () => {},
  onRowClick,
  layout,
  hideSelection = false,
}) => {
  const { data = [], isLoading } = itemState;
  return (
    <PMDataTable
      columns={columns}
      visibleKeys={visibleKeys}
      widths={widths}
      onResize={onResize}
      groupBy={groupBy}
      sort={sort}
      onSort={onSort}
      data={data}
      isLoading={isLoading}
      density={density}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onSelectionChange={onItemsSelect}
      onRowClick={onRowClick}
      layout={layout}
      hideSelection={hideSelection}
    />
  );
};

ChecklistsDataTable.defaultProps = {
  itemState: { data: [], isLoading: false },
  density: "comfortable",
  hasMore: false,
};
